import { createFileRoute, notFound, useLocation } from '@tanstack/react-router';
import { InvoiceScreen } from '@/features/buying/invoice/InvoiceScreen';
import type { InvoicePrefill } from '@/features/buying/invoice/types';

declare module '@tanstack/history' {
  interface HistoryState {
    /** Record invoice → "Enter on PO #N" (TASK-0236): the typed header and lines. */
    invoicePrefill?: InvoicePrefill;
  }
}

export type InvoiceSearch = { importId?: number; mode?: 'scan' | 'manual' };

export function validateInvoiceSearch(s: Record<string, unknown>): InvoiceSearch {
  const importId = Number(s.importId);
  const out: InvoiceSearch = {};
  if (Number.isInteger(importId) && importId > 0) out.importId = importId;
  if (s.mode === 'scan' || s.mode === 'manual') out.mode = s.mode;
  return out;
}

/**
 * /purchasing/$id/invoice — the invoice on its PO (TASK-0236, INC-8): "Scan invoice" (with
 * `importId`) and "Enter invoice" (`mode=manual`) record the vendor's invoice on this PO. Un-nested
 * like the order guide, so it does not inherit the PO page's tabs.
 */
export const Route = createFileRoute('/_app/purchasing_/$id_/invoice')({
  staticData: { crumb: 'Invoice' },
  validateSearch: validateInvoiceSearch,
  beforeLoad: ({ params }) => {
    if (!/^\d+$/.test(params.id)) throw notFound();
  },
  component: InvoiceRoute,
});

function InvoiceRoute() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const location = useLocation();
  const importId = search.importId ?? null;
  const mode = search.mode ?? (importId !== null ? 'scan' : 'manual');
  return (
    <InvoiceScreen
      key={`${id}:${importId ?? ''}:${mode}`}
      poId={Number(id)}
      importId={importId}
      mode={mode}
      prefill={location.state.invoicePrefill ?? null}
    />
  );
}
